import { ExternalLinkIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import { Badge, Flex, Link, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HELP_URLS } from 'classes/helpers/url.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSwitchInput } from 'components/common/form/switch';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { SettingButton } from 'components/common/settings-dialog/button';
import { SettingForm } from 'components/common/settings-dialog/form';
import { InactiveCallout } from 'components/common/settings-dialog/inactive-callout';
import { SettingRow } from 'components/common/settings-dialog/row';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  getDistanceOptions,
  MachineHelper,
} from 'lib_ts/classes/machine.helper';
import { numberToImperial } from 'lib_ts/classes/math.utilities';
import { TrainingHelper } from 'lib_ts/classes/training.helper';
import { VisionHelper } from 'lib_ts/classes/vision.helper';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { InstallStep } from 'lib_ts/enums/installation';
import {
  ServiceCategory,
  shouldCategoryResetTraining,
} from 'lib_ts/enums/machine-service-logs.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IInstallationDetails, IMachine } from 'lib_ts/interfaces/i-machine';
import { clone, first, isNil, merge } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { MachineServiceLogsService } from 'services/machine-service-logs.service';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  onSave: (payload: Partial<IMachine>) => void;
  onClose: () => void;
}

export const MachineGeneralTab = (props: IProps) => {
  const current = useAuthStore((s) => s.current);
  const isInGame = useAuthStore((s) => s.isInGame);

  const {
    machine,
    machineModels,
    isActive,
    createDataCollection,
    logPlateDistanceChange,
  } = useContext(MachineContext);

  const [value, setValue] = useState<Partial<IMachine>>({
    releaseFt: machine.releaseFt,
    training_threshold: machine.training_threshold,
    ball_type: machine.ball_type,
    nickname: machine.nickname,
    enable_auto_reset_ms: machine.enable_auto_reset_ms,
  });

  // dialog index refetches newest machine data upon mount, so we need to keep the local value state values in sync
  useEffect(() => {
    setValue({
      releaseFt: machine.releaseFt,
      training_threshold: machine.training_threshold,
      ball_type: machine.ball_type,
      nickname: machine.nickname,
      enable_auto_reset_ms: machine.enable_auto_reset_ms,
    });
  }, [
    machine.releaseFt,
    machine.training_threshold,
    machine.ball_type,
    machine.nickname,
    machine.enable_auto_reset_ms,
  ]);

  const trainingThresholdOptions = useMemo(
    () =>
      TrainingHelper.getTrainingThresholdOptions(
        value.training_threshold ?? null
      ),
    [value.training_threshold]
  );

  const ballTypeOptions = useMemo(() => {
    const output: IOptionExt[] = [];

    const relevantBallTypes = Object.values(BallType).filter((bt) =>
      machine.ball_type_options.includes(bt)
    );

    for (const bt of relevantBallTypes) {
      const modelID = machine.model_ids?.[bt];
      if (!modelID) {
        continue;
      }

      const model = machineModels.find((m) => m._id === modelID);
      if (!model) {
        continue;
      }

      const o: IOptionExt = {
        label: bt,
        value: bt,
        group: model.calibration_only ? '_2' : '_1',
        suffix: model.calibration_only ? (
          <Badge color={RADIX.COLOR.WARNING}>UNCAL</Badge>
        ) : undefined,
      };

      output.push(o);
    }

    return output;
  }, [machineModels, machine.ball_type_options]);

  const updateValue = useCallback(
    (payload: Partial<IMachine>) => {
      setValue((prev) => {
        const nextValue = clone(prev);
        merge(nextValue, payload);

        // also tells the parent to save the change
        props.onSave(nextValue);

        return nextValue;
      });
    },
    [props.onSave]
  );

  // is never updated until user leaves
  const [initialPlateDistance] = useState(machine.releaseFt);

  const warnPlateDistance = useMemo(
    () =>
      // no warning if not using rapsodo
      machine.tracking_device === TrackingDevice.RapsodoV3PRO &&
      // no warning if the value hasn't changed
      initialPlateDistance !== value.releaseFt,
    [initialPlateDistance, value.releaseFt, machine.tracking_device]
  );

  // whether an install exists and is incomplete
  const isInstalling = useMemo(
    () => machine.last_installation && !machine.last_installation.end_date,
    [machine.last_installation]
  );

  const [dialogBall, setDialogBall] = useState<{
    key: number;
    count: number;
  }>();

  const insetFt = useMemo(
    () => MachineHelper.getWheelsInsetFt(machine.hardware_version),
    [machine.hardware_version]
  );

  const plateDistanceFt = useMemo(() => {
    if (isNil(value.releaseFt)) {
      return undefined;
    }

    const output = Math.max(0, value.releaseFt - insetFt);

    return output;
  }, [value.releaseFt, insetFt]);

  const plateDistanceOption = useMemo(() => {
    if (isNil(plateDistanceFt)) {
      return undefined;
    }

    return MachineHelper.getDistanceOptionValue(plateDistanceFt);
  }, [plateDistanceFt]);

  const releaseDisplay = useMemo(() => {
    if (isNil(value.releaseFt)) {
      return undefined;
    }

    return numberToImperial(value.releaseFt).display;
  }, [value.releaseFt]);

  const distanceOptions = useMemo(
    () => getDistanceOptions(plateDistanceFt),
    [plateDistanceFt]
  );

  const onVision = machine.tracking_device === TrackingDevice.TrajektVision;
  const releaseSetByVision =
    VisionHelper.ALLOW_VISION_RELEASE_DISTANCE_UPDATE && onVision;

  return (
    <ErrorBoundary identifier="MachineGeneralTab">
      <SettingForm>
        {!isActive && <InactiveCallout />}

        <SettingRow
          header="settings.plate-distance"
          headerDecoration={
            releaseSetByVision ? (
              <Badge color={RADIX.COLOR.NEUTRAL}>
                {t('settings.set-by-vision')}
              </Badge>
            ) : undefined
          }
          description={
            releaseSetByVision
              ? t('settings.measured-distance-msg')
              : t('settings.tape-measured-distance-msg')
          }
          input={
            <CommonSelectInput
              id="machine-plate-distance"
              disabled={!isActive || releaseSetByVision}
              options={distanceOptions}
              value={plateDistanceOption?.value}
              onNumericChange={(nextPlateDistanceFt) => {
                if (nextPlateDistanceFt === plateDistanceFt) {
                  // no change necessary
                  return;
                }

                logPlateDistanceChange({
                  prevPlateFt: plateDistanceFt ?? null,
                  nextPlateFt: nextPlateDistanceFt,
                });

                updateValue({
                  releaseFt: nextPlateDistanceFt + insetFt,
                  last_plate_distance_ft: plateDistanceFt,
                  last_plate_distance_changed: new Date().toISOString(),
                  last_plate_distance_changedBy: current.email,
                });
              }}
              skipSort
              suppressWarning
            />
          }
        />

        <SettingRow
          separatorBefore
          header="settings.release-distance"
          description={t('settings.release-distance-msg')}
          input={
            <CommonTextInput
              id="machine-plate-release-distance"
              value={releaseDisplay}
              disabled
            />
          }
        />

        {warnPlateDistance && (
          <CommonCallout
            icon={<InfoCircledIcon />}
            flexAlign="start"
            text={t('settings.plate-distance-warning-msg').toString()}
            actions={[
              {
                size: '2',
                color: RADIX.COLOR.ACCENT,
                variant: RADIX.BUTTON.VARIANT.BORDERLESS,
                className: 'btn-floating',
                prefixIcon: <ExternalLinkIcon />,
                label: 'common.read-more',
                onClick: () => {
                  window.open(
                    t('common.intercom-url') + HELP_URLS.RAPSODO_PLATE_LOCATION,
                    '_blank'
                  );
                },
              },
            ]}
          />
        )}

        <SettingRow
          separatorBefore
          header="settings.training-shots"
          description={<Text>{t('settings.training-shots-msg')}</Text>}
          input={
            <CommonSelectInput
              id="machine-training-threshold"
              name="training_threshold"
              disabled={!isActive}
              options={trainingThresholdOptions}
              value={value.training_threshold?.toString()}
              onNumericChange={(v) => {
                if (v <= 0) {
                  return;
                }

                updateValue({
                  training_threshold: v,
                });
              }}
              skipSort
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.ball-type"
          description={
            <Text>
              <Trans
                i18nKey="settings.ball-type-msg"
                t={t}
                components={[
                  <Link
                    key="0"
                    href="https://trajektsports.myshopify.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                ]}
              />
            </Text>
          }
          input={
            <CommonSearchInput
              id="machine-ball-type"
              name="ball_type"
              disabled={!isActive}
              options={ballTypeOptions}
              values={value.ball_type ? [value.ball_type] : []}
              shouldChange={(v) => {
                const value = first(v) as BallType | undefined;

                if (!value) {
                  // invalid ball type
                  return false;
                }

                const modelID = machine.model_ids?.[value];
                if (!modelID) {
                  console.warn(`Failed to find ${value} in model_ids`);
                  return false;
                }

                const model = machineModels.find((m) => m._id === modelID);
                if (!model) {
                  console.warn(`Failed to find ${modelID} in machine models`);
                  return false;
                }

                if (!model.calibration_only) {
                  return true;
                }

                if (!isInstalling) {
                  // no install in progress, no data lost by replacing it with a standalone BTC
                  return true;
                }

                const { last_installation } = machine;

                if (!last_installation) {
                  // shouldn't trigger but it makes TS happy
                  return true;
                }

                if (last_installation.ball_type === value) {
                  // the install in progress is for the same ball type, fine to switch to it
                  return true;
                }

                NotifyHelper.warning({
                  message_md: `Please complete or cancel the installation for ${last_installation.ball_type} before trying again.`,
                });
                return false;
              }}
              onChange={async (v) => {
                const value = first(v) as BallType | undefined;

                if (!value) {
                  return;
                }

                const payload: Partial<IMachine> = {
                  ball_type: value,
                };

                const modelID = machine.model_ids?.[value];
                if (!modelID) {
                  console.warn(`Failed to find ${value} in model_ids`);
                  return;
                }

                const model = machineModels.find((m) => m._id === modelID);
                if (!model) {
                  console.warn(`Failed to find ${modelID} in machine models`);
                  return;
                }

                if (model.calibration_only && !isInstalling) {
                  // * it is necessary and safe to replace the last install w/ a standalone BTC
                  const btcDc = await createDataCollection(
                    DataCollectionType.BallTypeCalibration,
                    machine.machineID,
                    value
                  );

                  if (btcDc) {
                    const nextInstall: IInstallationDetails = {
                      standalone: InstallStep.BallType,
                      started_by: current.email,
                      start_date: new Date().toISOString(),
                      ball_type: value,
                      ball_type_locked: true,
                      btcID: btcDc._id,

                      end_date: null,
                      finished_by: null,
                      model_id: null,
                      rtID: null,

                      step_ball_type: null,
                      step_completion: null,
                      step_general_config: null,
                      step_hardware_changes: null,
                      step_projector_config: null,
                      step_repeatability: null,
                      step_test_shot: null,
                      step_vision: null,

                      vccID: null,
                      vcdFt: null,
                      vcdComplete: null,
                    };

                    payload.last_installation = nextInstall;
                  }
                }

                updateValue(payload);
              }}
              hideSearch
            />
          }
        />
        <SettingRow
          separatorBefore
          header="common.ball-replacement"
          description={<Text>{t('settings.ball-replacement-msg')}</Text>}
          input={
            <SettingButton
              label="common.ball-replacement"
              disabled={!isActive}
              onClick={() =>
                setDialogBall({
                  key: Date.now(),
                  count: 0,
                })
              }
            />
          }
        />

        <SettingRow
          separatorBefore
          header="settings.nickname"
          description={<Text>{t('settings.nickname-msg')}</Text>}
          input={
            <CommonTextInput
              id="machine-nickname"
              disabled={!isActive || isInGame}
              value={value.nickname}
              onChange={(v) => {
                if (v === value.nickname) {
                  return;
                }
                updateValue({
                  nickname: v,
                });
              }}
              debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
              optional
            />
          }
        />

        <SettingRow
          separatorBefore
          header="settings.auto-reset"
          description={<Text>{t('settings.auto-reset-msg')}</Text>}
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="machine-auto-reset"
                checked={value.enable_auto_reset_ms}
                onCheckedChange={(v) =>
                  updateValue({
                    enable_auto_reset_ms: v,
                  })
                }
              />
            </Flex>
          }
        />
      </SettingForm>

      {dialogBall && (
        <CommonDialog
          key={dialogBall.key}
          identifier="SettingsBallReplacement"
          title="common.ball-replacement"
          width={RADIX.DIALOG.WIDTH.SM}
          content={
            <Flex direction="column" gap="4">
              <Text>
                Please remove all existing <Badge>{machine.ball_type}</Badge>{' '}
                balls from the hopper before replacing them with the new batch.
              </Text>

              <Text>
                Use the input below to specify the number of new{' '}
                <Badge>{machine.ball_type}</Badge> balls being used going
                forward.
              </Text>

              <CommonTextInput
                id="ball_count"
                label="Ball Count"
                value={dialogBall.count.toString()}
                onNumericChange={(v) => {
                  if (!v) {
                    return;
                  }

                  setDialogBall((prev) => {
                    if (!prev) {
                      return prev;
                    }

                    return {
                      ...prev,
                      count: v,
                    };
                  });
                }}
              />
            </Flex>
          }
          buttons={[
            {
              label: 'Confirm',
              onClick: async () => {
                if (dialogBall.count <= 0) {
                  NotifyHelper.warning({
                    message_md:
                      'Please enter a positive value for ball count and try again.',
                  });
                  return;
                }

                const category = ServiceCategory.BallReplacement;

                await MachineServiceLogsService.getInstance().createOne({
                  machineID: machine.machineID,
                  serviced: new Date().toISOString(),
                  servicedBy: current.email,
                  category: category,
                  ball_type: machine.ball_type,
                  ball_count: dialogBall.count,
                  notes: 'Created by user via settings dialog',
                  trainingReset: shouldCategoryResetTraining(category),
                });

                NotifyHelper.success({
                  message_md: 'Ball replacement logged!',
                });

                setDialogBall(undefined);
              },
            },
          ]}
          onClose={() => setDialogBall(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
